import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Typography, Paper, List, ListItem, ListItemButton, ListItemText,
    Chip, Skeleton, TextField, InputAdornment, Grid, Divider, IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import DraftsIcon from '@mui/icons-material/Drafts';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { EmailListItem, Email, EmailType } from '../../types/email';
import { getUserEmails, getEmail } from '../../services/api';
import { AuthContext } from '../../context/AuthContext';
import { designTokens } from '../../theme/designTokens';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const EmailsPage = () => {
    const { user } = useContext(AuthContext) || {};
    const [emails, setEmails] = useState<EmailListItem[]>([]);
    const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<EmailType | 'all'>('all');

    useEffect(() => {
        if (user?._id) {
            fetchEmails();
        }
    }, [user, filterType]);

    const fetchEmails = async () => {
        try {
            setLoading(true);
            const params = filterType !== 'all' ? { type: filterType } : {};
            const response = await getUserEmails(user!._id, params);
            setEmails(response.data);
        } catch (error) {
            console.error('Error fetching emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEmailClick = async (emailId: string) => {
        try {
            setLoadingDetail(true);
            const email = await getEmail(emailId);
            setSelectedEmail(email);
        } catch (error) {
            console.error('Error fetching email:', error);
        } finally {
            setLoadingDetail(false);
        }
    };

    const getEmailTypeLabel = (type: EmailType): string => {
        const labels: Record<EmailType, string> = {
            welcome: 'Bienvenue',
            password_reset: 'Mot de passe',
            evaluation: 'Évaluation',
            follow_up: 'Suivi',
            notification: 'Notification',
            other: 'Autre'
        };
        return labels[type];
    };

    const getEmailTypeColor = (type: EmailType): string => {
        const colors: Record<EmailType, string> = {
            welcome: '#667eea',
            password_reset: '#f093fb',
            evaluation: '#4facfe',
            follow_up: '#43e97b',
            notification: '#fa709a',
            other: '#9ca3af'
        };
        return colors[type];
    };

    const filteredEmails = emails.filter(email =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#1a237e', fontFamily: 'Quicksand, sans-serif', mb: 3 }}>
                📧 Mes Emails
            </Typography>

            <Grid container spacing={2} sx={{ height: 'calc(100% - 60px)' }}>
                {/* Email List */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ ...designTokens.glassCard, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Rechercher un email..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ fontFamily: 'Quicksand, sans-serif', mb: 2 }}
                            />

                            {/* Filter Chips */}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                    label="Tous"
                                    size="small"
                                    onClick={() => setFilterType('all')}
                                    sx={{
                                        bgcolor: filterType === 'all' ? '#667eea' : 'rgba(0,0,0,0.05)',
                                        color: filterType === 'all' ? 'white' : 'inherit',
                                        fontFamily: 'Quicksand, sans-serif'
                                    }}
                                />
                                {(['welcome', 'evaluation', 'follow_up', 'notification'] as EmailType[]).map(type => (
                                    <Chip
                                        key={type}
                                        label={getEmailTypeLabel(type)}
                                        size="small"
                                        onClick={() => setFilterType(type)}
                                        sx={{
                                            bgcolor: filterType === type ? getEmailTypeColor(type) : 'rgba(0,0,0,0.05)',
                                            color: filterType === type ? 'white' : 'inherit',
                                            fontFamily: 'Quicksand, sans-serif'
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <List sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
                            {loading ? (
                                [1, 2, 3, 4, 5].map((i) => (
                                    <ListItem key={i}>
                                        <ListItemText
                                            primary={<Skeleton width="80%" />}
                                            secondary={<Skeleton width="60%" />}
                                        />
                                    </ListItem>
                                ))
                            ) : filteredEmails.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                    <EmailIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                    <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                        Aucun email
                                    </Typography>
                                </Box>
                            ) : (
                                filteredEmails.map((email) => (
                                    <ListItemButton
                                        key={email._id}
                                        selected={selectedEmail?._id === email._id}
                                        onClick={() => handleEmailClick(email._id)}
                                        sx={{
                                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                                            '&.Mui-selected': {
                                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                                                '&:hover': {
                                                    bgcolor: 'rgba(102, 126, 234, 0.15)',
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
                                                    {email.subject}
                                                </Typography>
                                                {email.status === 'failed' && (
                                                    <ErrorIcon sx={{ fontSize: 16, color: '#dc3545' }} />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={getEmailTypeLabel(email.type)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: getEmailTypeColor(email.type),
                                                        color: 'white',
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                        fontFamily: 'Quicksand, sans-serif'
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                                    {email.sentAt ? formatDistanceToNow(new Date(email.sentAt), { addSuffix: true, locale: fr }) : 'En attente'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </ListItemButton>
                                ))
                            )}
                        </List>
                    </Paper>
                </Grid>

                {/* Email Detail */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ ...designTokens.glassCard, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {!selectedEmail ? (
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <DraftsIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                                    <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                        Sélectionnez un email pour le lire
                                    </Typography>
                                </Box>
                            </Box>
                        ) : loadingDetail ? (
                            <Box sx={{ p: 3 }}>
                                <Skeleton variant="text" width="60%" height={40} />
                                <Skeleton variant="text" width="40%" />
                                <Divider sx={{ my: 2 }} />
                                <Skeleton variant="rectangular" height={300} />
                            </Box>
                        ) : (
                            <>
                                {/* Email Header */}
                                <Box sx={{ p: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                            {selectedEmail.subject}
                                        </Typography>
                                        <IconButton size="small" onClick={() => setSelectedEmail(null)}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Chip
                                            label={getEmailTypeLabel(selectedEmail.type)}
                                            size="small"
                                            sx={{
                                                bgcolor: getEmailTypeColor(selectedEmail.type),
                                                color: 'white',
                                                fontFamily: 'Quicksand, sans-serif'
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                            {selectedEmail.sentAt ? formatDistanceToNow(new Date(selectedEmail.sentAt), { addSuffix: true, locale: fr }) : 'En attente'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Email Content */}
                                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                                    <div dangerouslySetInnerHTML={{ __html: selectedEmail.htmlContent }} />
                                </Box>
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default EmailsPage;
